import { useNavigate } from 'react-router-dom'

import {
  Box,
  Icon,
  Row,
  Text,
  Title,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useGetIncidenceSettings } from 'modules/bookkeepers/hooks/useGetIncidenceSettings'
import { FACTORIAL_APP, toIncidencesSettings } from 'modules/bookkeepers/lib/amplitudeData'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface Props {
  canUpsellPayroll?: boolean
}
const Content = ({ canUpsellPayroll }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    company: { id: companyId, name: companyName },
  } = useCurrentSession()
  const {
    loadingFeatures,
    has_inhouse_payroll: hasInhousePayroll,
    has_factorial_professionals: hasProfessionals,
  } = useFeatures(['inhouse_payroll', 'factorial_professionals'])
  const { hasCompensations, loading: loadingCompensations } = useHasCompensations()
  const { trackedEventsCount } = useGetIncidenceSettings(companyId, false)
  const [open, toggle] = useToggle(false)
  const navigateToManageIncidences = (source: string) => {
    track(trackingEvents.bookkeeper.incidences.goToIncidencesSettings, {
      application: FACTORIAL_APP,
      company_name: companyName,
      has_professionals: hasProfessionals,
      has_in_house_payroll: hasInhousePayroll,
      source: source,
    })

    return hasCompensations
      ? navigate(navigation.settings.compensations.employeeUpdates.fullPath())
      : navigate(navigation.settings.payroll_bundle.incidences.fullPath())
  }

  if (loadingFeatures || loadingCompensations) return <Loading />

  return (
    <Box
      cursor={canUpsellPayroll ? 'default' : 'pointer'}
      onClick={() => {
        if (canUpsellPayroll) {
          return
        }
        navigateToManageIncidences(toIncidencesSettings.fromPayrollSettings)
      }}
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs016',
      }}
      gap="s8"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      padding="s16"
    >
      <Box gap="s4">
        <Text.Normal color="grey1000" weight="medium">
          {i18n.t('app_store_v2.apps.payroll_bundle.settings.employee_updates.title')}
        </Text.Normal>
        <Text.Normal color="grey800" weight="normal" size="200">
          {i18n.t('app_store_v2.apps.payroll_bundle.settings.incidences.tracked_events', {
            events_count: canUpsellPayroll ? 0 : trackedEventsCount,
          })}
        </Text.Normal>
      </Box>
      <Box
        padding="s2"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'rel500',
        }}
      >
        <Tooltip.Alt
          attachment="bottom center"
          open={open}
          toggle={toggle}
          label={
            canUpsellPayroll ? i18n.t('compensations.compensations_product_disabled_tooltip') : ''
          }
        >
          <Icon.Medium icon="arrow-right" />
        </Tooltip.Alt>
      </Box>
    </Box>
  )
}

const EmployeeUpdates = ({ canUpsellPayroll }: Props) => {
  return (
    <Box flexDirection="row" alignItems="center" paddingX="s64" paddingY="s32" width="full">
      <Row>
        <Row.Col halfWidth>
          <Box paddingRight="s64">
            <Title
              variant="500"
              title={i18n.t(
                'app_store_v2.apps.payroll_bundle.settings.employee_updates.section_title'
              )}
              description={i18n.t(
                'app_store_v2.apps.payroll_bundle.settings.employee_updates.section_description'
              )}
              illustration="bell"
            />
            {canUpsellPayroll && (
              <UpsellingKit.UpsellButton
                moduleRequested={AvailableModules.compensations}
                requestSource="employee_updates_settings_page"
                pageSource="employee_updates_settings_page"
                requestReason={ValidReasons.MissingAddon}
                showModuleModal
                size="md"
                variant="outlinePromote"
                label={i18n.t('compensations.upsell_button')}
              />
            )}
          </Box>
        </Row.Col>
        <Row.Col halfWidth>
          <Content canUpsellPayroll={canUpsellPayroll} />
        </Row.Col>
      </Row>
    </Box>
  )
}

export default EmployeeUpdates
